import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, 
  ExternalLink, 
  Plus, 
  Link as LinkIcon,
  AlertCircle,
  RefreshCw,
  FolderOpen
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
// drive.file grants per-file access to files opened via the Picker. It is the
// narrowest scope that lets us add permissions on the picked doc — readonly
// would let us list but not share.
const SCOPES = 'https://www.googleapis.com/auth/drive.file';

const DEFAULT_DOC_ROLES = [
  { value: 'DRAFT', label: 'Draft', description: 'Working document' },
  { value: 'PLANNING', label: 'Planning', description: 'Planning notes' },
  { value: 'REFLECTION', label: 'Reflection', description: 'RPPF or reflection' },
  { value: 'FEEDBACK', label: 'Feedback', description: 'Teacher feedback' },
  { value: 'OTHER', label: 'Other', description: 'Other document' },
];

// Load Google API scripts
const loadGoogleScripts = () => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.gapi && window.google?.picker) {
      resolve();
      return;
    }

    // Load GAPI
    const gapiScript = document.createElement('script');
    gapiScript.src = 'https://apis.google.com/js/api.js';
    gapiScript.async = true;
    gapiScript.defer = true;
    
    // Load GSI (Google Sign-In)
    const gsiScript = document.createElement('script');
    gsiScript.src = 'https://accounts.google.com/gsi/client';
    gsiScript.async = true;
    gsiScript.defer = true;

    let loadedCount = 0;
    const checkLoaded = () => {
      loadedCount++;
      if (loadedCount === 2) {
        // Wait for gapi to be ready
        window.gapi.load('picker', () => {
          resolve();
        });
      }
    };

    gapiScript.onload = checkLoaded;
    gsiScript.onload = checkLoaded;
    gapiScript.onerror = reject;
    gsiScript.onerror = reject;

    document.body.appendChild(gapiScript);
    document.body.appendChild(gsiScript);
  });
};

const GoogleDocsPicker = ({
  onDocSelected,
  projectId,
  buttonText = 'Attach Google Doc',
  buttonVariant = 'outline',
  buttonSize = 'default',
  showIcon = true,
  className = '',
  docRoles = DEFAULT_DOC_ROLES,
  // Comma-separated Google Picker mime types. Defaults to Google Docs only
  // (existing behavior for Projects/Essays); pass a broader list to also
  // surface Sheets/Slides/PDFs (e.g. the College Docs tab).
  mimeTypes = 'application/vnd.google-apps.document'
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [tokenClient, setTokenClient] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [error, setError] = useState(null);
  const [showFallback, setShowFallback] = useState(false);
  const [fallbackData, setFallbackData] = useState({
    url: '',
    title: '',
    role: 'DRAFT',
    isCurrent: false
  });

  // Load scripts on mount
  useEffect(() => {
    loadGoogleScripts()
      .then(() => {
        setScriptsLoaded(true);
        // Initialize token client
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: SCOPES,
          callback: (response) => {
            if (response.access_token) {
              setAccessToken(response.access_token);
              openPicker(response.access_token);
            }
          },
        });
        setTokenClient(client);
      })
      .catch((err) => {
        console.error('Failed to load Google scripts:', err);
        setError('Unable to load Google Drive. You can paste a link instead.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPicker = useCallback((token) => {
    if (!window.google?.picker) {
      setError('Google Picker not available');
      return;
    }

    const view = new window.google.picker.DocsView()
      .setIncludeFolders(false)
      .setMimeTypes(mimeTypes)
      .setMode(window.google.picker.DocsViewMode.LIST);

    const picker = new window.google.picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(token)
      .setDeveloperKey(GOOGLE_API_KEY)
      .setCallback((data) => {
        if (data.action === window.google.picker.Action.PICKED) {
          const doc = data.docs[0];
          // Pass the token that opened this picker, not whatever is in state:
          // by the time the user actually picks, state might have gone stale
          // if there was a fresh sign-in in between.
          handleDocPicked(
            {
              id: doc.id,
              name: doc.name,
              url: doc.url,
              mimeType: doc.mimeType,
              lastEditedUtc: doc.lastEditedUtc
            },
            token
          );
        }
        setIsLoading(false);
      })
      .setTitle('Select a Google Doc')
      .build();

    picker.setVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mimeTypes]);

  const handlePickerClick = () => {
    setIsLoading(true);
    setError(null);

    if (!scriptsLoaded || !tokenClient) {
      setError('Google Drive is loading. Please try again.');
      setIsLoading(false);
      return;
    }

    // Request access token
    if (accessToken) {
      openPicker(accessToken);
    } else {
      tokenClient.requestAccessToken({ prompt: '' });
    }
  };

  const handleDocPicked = (doc, tokenForShare) => {
    onDocSelected({
      url: doc.url || `https://docs.google.com/document/d/${doc.id}/edit`,
      title: doc.name,
      googleDocId: doc.id,
      mimeType: doc.mimeType,
      role: 'DRAFT',
      isCurrent: false,
      projectId,
      // The caller can use this to add Drive permissions on the picked file
      // without asking the user to authenticate again. Tokens expire quickly,
      // so consumers should share right after receiving this.
      accessToken: tokenForShare || accessToken
    });
    toast.success(`"${doc.name}" attached`);
  };

  const handleFallbackSubmit = (e) => {
    e.preventDefault();
    if (!fallbackData.url.trim()) {
      toast.error('Please enter a document URL');
      return;
    }

    // Extract doc ID from URL if possible
    const docIdMatch = fallbackData.url.match(/\/d\/([a-zA-Z0-9-_]+)/);
    
    onDocSelected({
      url: fallbackData.url,
      title: fallbackData.title || 'Google Doc',
      googleDocId: docIdMatch?.[1] || null,
      role: fallbackData.role,
      isCurrent: fallbackData.isCurrent,
      projectId
    });
    
    setShowFallback(false);
    setFallbackData({ url: '', title: '', role: 'DRAFT', isCurrent: false });
    toast.success('Document attached');
  };

  return (
    <>
      <Button
        variant={buttonVariant}
        size={buttonSize}
        onClick={handlePickerClick}
        disabled={isLoading}
        className={className}
        data-testid="attach-google-doc-btn"
      >
        {isLoading ? (
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
        ) : showIcon ? (
          <FolderOpen className="w-4 h-4 mr-2" />
        ) : null}
        {buttonText}
      </Button>

      {error && (
        <div className="mt-2 space-y-2">
          <Alert className="border-amber-200 bg-amber-50">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 text-sm">
              {error}
            </AlertDescription>
          </Alert>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setError(null); handlePickerClick(); }}
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Try again
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setError(null); setShowFallback(true); }}
            >
              <LinkIcon className="w-3 h-3 mr-1" />
              Paste link instead
            </Button>
          </div>
        </div>
      )}

      {/* Fallback Dialog */}
      <Dialog open={showFallback} onOpenChange={setShowFallback}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attach Google Doc by Link</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFallbackSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Document URL *</Label>
              <Input
                value={fallbackData.url}
                onChange={(e) => setFallbackData({ ...fallbackData, url: e.target.value })}
                placeholder="https://docs.google.com/document/d/..."
                data-testid="fallback-url-input"
              />
              <p className="text-xs text-slate-500">
                Paste the full URL from your Google Doc's address bar
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Document Title</Label>
              <Input
                value={fallbackData.title}
                onChange={(e) => setFallbackData({ ...fallbackData, title: e.target.value })}
                placeholder="e.g., EE Draft 2"
              />
            </div>

            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select 
                value={fallbackData.role} 
                onValueChange={(v) => setFallbackData({ ...fallbackData, role: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {docRoles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="fallbackCurrent"
                checked={fallbackData.isCurrent}
                onCheckedChange={(c) => setFallbackData({ ...fallbackData, isCurrent: c })}
              />
              <Label htmlFor="fallbackCurrent" className="text-sm">Set as current working document</Label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowFallback(false)}>
                Cancel
              </Button>
              <Button type="submit">Attach Document</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GoogleDocsPicker;
